const GOOGLE_AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';
const GOOGLE_TOKEN_ENDPOINT = 'https://oauth2.googleapis.com/token';

/**
 * Lightweight PKCE OAuth client for Google Identity services.
 * Handles auth URL generation, token exchange, and refresh logic.
 */
export class GoogleOAuthClient {
  constructor(options = {}) {
    this.clientId = options.clientId || '';
    this.redirectUri = options.redirectUri || this.defaultRedirectUri();
    this.scopes = options.scopes || ['https://www.googleapis.com/auth/calendar.readonly'];
    this.accessType = options.accessType || 'offline';
    this.prompt = options.prompt || 'consent';
    this.codeVerifierLength = options.codeVerifierLength || 64;
    this.clockSkewMs = options.clockSkewMs ?? 30_000;
    this.state = options.state || 'life-dashboard';
    this.tokens = null;
    this.codeVerifier = null;
    this.storage = options.storage ?? window.sessionStorage;
    this.storageKey = options.storageKey || 'googleOAuthTokens';
    this.pkceStorageKey = `${this.storageKey}:verifier`;
    this.defaultScopes = [...this.scopes];
  }

  defaultRedirectUri() {
    const origin = window.location.origin;
    return `${origin.replace(/\/$/, '')}/oauth-callback.html`;
  }

  async initialize() {
    if (!this.clientId) {
      throw new Error('[GoogleOAuthClient] Missing clientId. Set it before initializing OAuth.');
    }
    this.restoreTokens();
  }

  restoreTokens() {
    if (!this.storage) return;
    try {
      const raw = this.storage.getItem(this.storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.accessToken) {
        this.tokens = parsed;
      }
    } catch (error) {
      console.warn('[GoogleOAuthClient] Failed to restore tokens', error);
    }
  }

  persistTokens(tokens) {
    this.tokens = tokens;
    if (!this.storage) return;
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(tokens));
    } catch (error) {
      console.warn('[GoogleOAuthClient] Failed to persist tokens', error);
    }
  }

  clearTokens() {
    this.tokens = null;
    if (!this.storage) return;
    try {
      this.storage.removeItem(this.storageKey);
      this.storage.removeItem(this.pkceStorageKey);
    } catch (error) {
      console.warn('[GoogleOAuthClient] Failed to clear tokens', error);
    }
  }

  async startAuthorization() {
    this.codeVerifier = this.generateCodeVerifier(this.codeVerifierLength);
    const challenge = await this.generateCodeChallenge(this.codeVerifier);

    if (this.storage) {
      try {
        this.storage.setItem(this.pkceStorageKey, this.codeVerifier);
      } catch (error) {
        console.warn('[GoogleOAuthClient] Unable to persist PKCE verifier', error);
      }
    }

    const authUrl = new URL(GOOGLE_AUTH_ENDPOINT);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('client_id', this.clientId);
    authUrl.searchParams.set('redirect_uri', this.redirectUri);
    authUrl.searchParams.set('scope', this.scopes.join(' '));
    authUrl.searchParams.set('code_challenge', challenge);
    authUrl.searchParams.set('code_challenge_method', 'S256');
    authUrl.searchParams.set('access_type', this.accessType);
    authUrl.searchParams.set('prompt', this.prompt);
    authUrl.searchParams.set('state', this.state);

    return authUrl.toString();
  }

  async exchangeCodeForTokens(code) {
    const verifier = this.getPersistedVerifier();
    if (!verifier) {
      throw new Error('[GoogleOAuthClient] Missing PKCE verifier; restart the auth flow.');
    }

    const body = new URLSearchParams({
      client_id: this.clientId,
      code,
      code_verifier: verifier,
      redirect_uri: this.redirectUri,
      grant_type: 'authorization_code',
    });

    const response = await fetch(GOOGLE_TOKEN_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`[GoogleOAuthClient] Token exchange failed: ${errorText}`);
    }

    const payload = await response.json();
    const expiry = Date.now() + (payload.expires_in * 1000);
    const tokens = {
      accessToken: payload.access_token,
      refreshToken: payload.refresh_token || null,
      scope: payload.scope,
      tokenType: payload.token_type,
      expiresAt: expiry,
    };

    this.persistTokens(tokens);
    return tokens;
  }

  async refreshAccessToken() {
    if (!this.tokens?.refreshToken) {
      throw new Error('[GoogleOAuthClient] No refresh token available.');
    }

    const body = new URLSearchParams({
      client_id: this.clientId,
      refresh_token: this.tokens.refreshToken,
      grant_type: 'refresh_token',
    });

    const response = await fetch(GOOGLE_TOKEN_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body,
    });

    if (!response.ok) {
      const errorText = await response.text();
      this.clearTokens();
      throw new Error(`[GoogleOAuthClient] Refresh failed: ${errorText}`);
    }

    const payload = await response.json();
    const expiry = Date.now() + (payload.expires_in * 1000);
    const tokens = {
      ...this.tokens,
      accessToken: payload.access_token,
      expiresAt: expiry,
      scope: payload.scope || this.tokens.scope,
      tokenType: payload.token_type || this.tokens.tokenType,
    };

    this.persistTokens(tokens);
    return tokens;
  }

  async getAccessToken() {
    if (!this.tokens?.accessToken) {
      return null;
    }

    const expiresSoon = (this.tokens.expiresAt - this.clockSkewMs) <= Date.now();
    if (expiresSoon) {
      await this.refreshAccessToken();
    }
    return this.tokens.accessToken;
  }

  setScopes(scopes = []) {
    if (!Array.isArray(scopes) || scopes.length === 0) {
      return;
    }
    const normalized = Array.from(new Set(scopes.filter(Boolean)));
    if (normalized.length > 0) {
      this.scopes = normalized;
    }
  }

  resetScopesToDefault() {
    if (Array.isArray(this.defaultScopes) && this.defaultScopes.length) {
      this.scopes = [...this.defaultScopes];
    }
  }

  getGrantedScopes() {
    const scopeString = this.tokens?.scope || '';
    if (!scopeString) {
      return [];
    }
    return scopeString
      .split(/\s+/)
      .map((scope) => scope.trim())
      .filter(Boolean);
  }

  hasScopes(requiredScopes = []) {
    if (!Array.isArray(requiredScopes) || requiredScopes.length === 0) {
      return true;
    }
    const granted = new Set(this.getGrantedScopes());
    return requiredScopes.every((scope) => granted.has(scope));
  }

  getPersistedVerifier() {
    if (this.codeVerifier) {
      return this.codeVerifier;
    }
    if (!this.storage) return null;
    try {
      const stored = this.storage.getItem(this.pkceStorageKey);
      return stored || null;
    } catch (error) {
      console.warn('[GoogleOAuthClient] Unable to read PKCE verifier', error);
      return null;
    }
  }

  generateCodeVerifier(length) {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const randomValues = new Uint32Array(length);
    window.crypto.getRandomValues(randomValues);
    let verifier = '';
    for (let i = 0; i < randomValues.length; i += 1) {
      verifier += charset[randomValues[i] % charset.length];
    }
    return verifier;
  }

  async generateCodeChallenge(verifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    if (!window.crypto?.subtle) {
      throw new Error('[GoogleOAuthClient] SubtleCrypto not available for PKCE challenge.');
    }

    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(digest);
  }

  base64UrlEncode(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}

GoogleOAuthClient.AUTH_ENDPOINT = GOOGLE_AUTH_ENDPOINT;
GoogleOAuthClient.TOKEN_ENDPOINT = GOOGLE_TOKEN_ENDPOINT;
